<template>
  <div class="sql-editor" :class="{ 'is-fullscreen': isFullScreen }">
    <div class="sql-editor-bar">
      <div class="bar-left">
        <span class="sql-badge" :class="{ 'sql-badge--php': ehPhp }">{{ ehPhp ? 'PHP' : 'SQL' }}</span>
        <span class="sql-lines">{{ nLinhas }} linha{{ nLinhas === 1 ? '' : 's' }}</span>
      </div>
      <div class="bar-right">
        <button v-if="!ehPhp" type="button" class="bar-btn" :disabled="readonly || !codigo.trim()" @click="formatar" title="Formatar SQL (Ctrl+Alt+L) — com texto selecionado, formata só a seleção">
          <fa-icon icon="magic" /> Formatar
        </button>
        <button type="button" class="bar-btn" @click="toggleFullScreen" title="Editar em tela cheia (F11)">
          <fa-icon icon="expand" /> Editar em tela cheia
        </button>
      </div>
    </div>

    <codemirror
      ref="cm"
      v-model="codigo"
      :options="codeOptions"
      class="sql-cm"
      @ready="onReady"
    />

    <div v-if="isFullScreen" class="fs-actions">
      <button v-if="executavel" type="button" class="fs-btn fs-btn--run" @click="$emit('executar')">
        <fa-icon icon="play" /> Executar query
      </button>
      <button type="button" class="fs-btn" @click="toggleFullScreen">
        <fa-icon icon="times" /> Sair da tela cheia
      </button>
    </div>
  </div>
</template>

<script>
import CodeMirror from "codemirror";
import { codemirror } from "vue-codemirror";
import { formatRiver } from "@/helpers/sql/sqlRiverFormatter";
import { ehPhp, extractAliases } from "@/helpers/sql/sqlErpHelpers";
import cmFullscreenMixin from "@/components/form/cmFullscreenMixin";

import "codemirror/lib/codemirror.css";
import "codemirror/addon/display/fullscreen.css";
import "codemirror/addon/display/fullscreen.js";
import "codemirror/addon/selection/active-line.js";
import "codemirror/addon/display/autorefresh.js";
import "codemirror/addon/hint/show-hint.css";
import "codemirror/addon/hint/show-hint.js";
import "./codemirrorHints.css";
import "codemirror/mode/sql/sql.js";

const SQL_KEYWORDS = [
  "SELECT", "FROM", "WHERE", "AS", "JOIN", "LEFT", "RIGHT", "INNER", "OUTER",
  "ON", "AND", "OR", "NOT", "IN", "IS", "NULL", "GROUP BY", "ORDER BY",
  "HAVING", "DISTINCT", "CASE", "WHEN", "THEN", "ELSE", "END", "COALESCE",
  "SUBSTR", "LOWER", "UPPER", "CONCAT", "REPLACE", "TRIM", "CAST", "COUNT",
  "SUM", "MAX", "MIN",
];

const KEYWORDS = /^(SELECT|FROM|WHERE|AS|NULL|JOIN|LEFT|RIGHT|INNER|OUTER|FULL|CROSS|ON|AND|OR|NOT|IN|IS|GROUP|BY|ORDER|HAVING|LIMIT|OFFSET|DISTINCT|UNION|ALL|CASE|WHEN|THEN|ELSE|END|INSERT|INTO|VALUES|UPDATE|SET|DELETE|EXISTS|LIKE|ILIKE|BETWEEN|ASC|DESC|WITH|TOP|OVER|PARTITION)$/i;

// Modo próprio que dirige todo o realce, garantindo as cores do tema VersoTech:
// keyword=rosa, função=azul, tabela(antes do ponto)=clara, coluna(após ponto)=roxa,
// número=laranja, string=verde, comentário=cinza itálico, pontuação=neutra.
//
// É um modo COM estado (não um overlay): o CodeMirror proíbe overlays com estado
// ("Overlays may not be stateful"), e precisamos de estado para comentários de
// bloco /* ... */ que atravessam várias linhas.
function vtSqlMode() {
  return {
    // Rastreia se estamos dentro de um bloco /* ... */ aberto numa linha anterior.
    startState() {
      return { inBlock: false };
    },
    copyState(s) {
      return { inBlock: s.inBlock };
    },
    token(stream, state) {
      // Continuação de um comentário de bloco /* ... */ iniciado acima.
      if (state.inBlock) {
        if (stream.skipTo("*/")) {
          stream.match("*/");
          state.inBlock = false;
        } else {
          stream.skipToEnd();
        }
        return "vt-comment";
      }

      if (stream.eatSpace()) return null;

      // strings (antes dos comentários: um /* dentro de string não abre bloco)
      if (stream.match(/^'(?:[^'\\]|\\.)*'?/)) return "vt-str";
      if (stream.match(/^"(?:[^"\\]|\\.)*"?/)) return "vt-str";
      // comentário de bloco /* ... */ (pode atravessar várias linhas)
      if (stream.match("/*")) {
        if (stream.skipTo("*/")) {
          stream.match("*/");
        } else {
          stream.skipToEnd();
          state.inBlock = true;
        }
        return "vt-comment";
      }
      // comentário de linha
      if (stream.match(/^--.*/)) return "vt-comment";
      // número
      if (stream.match(/^\d+(?:\.\d+)?/)) return "vt-num";
      // coluna após o ponto: .CID_CODCID
      if (stream.match(/^\.\s*[A-Za-z_][\w$]*/)) return "vt-col";
      // identificador
      if (stream.match(/^[A-Za-z_][\w$]*/)) {
        const palavra = stream.current().trim();
        if (KEYWORDS.test(palavra)) return "vt-kw";
        const resto = stream.string.slice(stream.pos);
        if (/^\s*\(/.test(resto)) return "vt-fn";   // função: seguida de (
        if (/^\s*\./.test(resto)) return "vt-tbl";  // tabela: seguida de .
        return "vt-ident";
      }
      // pontuação
      if (stream.match(/^[(),*]/)) return "vt-punc";

      stream.next();
      return null;
    },
  };
}

// Registra o modo uma única vez (no carregamento do módulo, antes do editor montar).
if (!CodeMirror.modes || !CodeMirror.modes.vtsql) {
  CodeMirror.defineMode("vtsql", vtSqlMode);
}

export default {
  name: "SqlEditor",
  components: { codemirror },
  mixins: [cmFullscreenMixin],
  props: {
    value: { type: String, default: "" },
    readonly: { type: Boolean, default: false },
    // Campos fillable do model selecionado (para o autocomplete).
    campos: { type: Array, default: () => [] },
    // Tipo persistido da view ("sql" | "php"); fonte de verdade vinda do backend.
    tipo: { type: String, default: "" },
    sugestoes: { type: Array, default: () => [] },
    executavel: { type: Boolean, default: true },
  },
  data() {
    return {
      codigo: this.value || "",
      cm: null,
    };
  },
  computed: {
    nLinhas() {
      return (this.codigo || "").split("\n").length;
    },
    ehPhp() {
      return ehPhp(this.tipo);
    },
    sugestoesExtras() {
      return (this.sugestoes || [])
        .map((sugestao) => (typeof sugestao === "string" ? { texto: sugestao, descricao: "" } : sugestao))
        .filter((sugestao) => sugestao && sugestao.texto);
    },
    codeOptions() {
      return {
        tabSize: 4,
        lineNumbers: true,
        styleActiveLine: true,
        lineWrapping: false,
        mode: "vtsql",
        theme: "vtdark",
        autoRefresh: true,
        readOnly: this.readonly,
        extraKeys: {
          F11: () => this.toggleFullScreen(),
          "Ctrl-Alt-L": () => this.formatar(),
          "Shift-Ctrl-F": () => this.formatar(),
          "Ctrl-Space": (cm) => cm.showHint(this.opcoesSugestoes()),
        },
      };
    },
  },
  watch: {
    codigo(novo) {
      this.$emit("input", novo);
    },
    value(novo) {
      if (novo !== this.codigo) this.codigo = novo;
    },
  },
  beforeDestroy() {
    document.removeEventListener("scroll", this.fecharSugestoesAoRolar, true);
  },
  methods: {
    onReady(cm) {
      this.cm = cm;
      document.addEventListener("scroll", this.fecharSugestoesAoRolar, true);
      cm.on("inputRead", (instance, change) => {
        const ch = change.text && change.text[0];
        const abreSugestaoExtra = ch === ":" && this.sugestoesExtras.length;
        if (ch && (/[\w]/.test(ch) || abreSugestaoExtra)) {
          instance.showHint(this.opcoesSugestoes());
        }
      });
    },
    opcoesSugestoes() {
      const editor = this.cm && this.cm.getWrapperElement();

      return {
        hint: (c) => this.hint(c),
        completeSingle: false,
        container: (editor && editor.closest(".v--modal-overlay")) || document.body,
      };
    },
    // Lista de candidatos: campos do model (maiúsculo), aliases (AS …) e
    // tabelas/aliases referenciados na query, além de palavras-chave SQL.
    candidatos() {
      const code = this.codigo || "";
      const set = new Set();

      (this.campos || []).forEach((c) => set.add(String(c).toUpperCase()));

      extractAliases(code).forEach((alias) => set.add(alias));

      let m;
      const reTabela = /\b([A-Za-z_][\w$]*)\s*\./g;
      while ((m = reTabela.exec(code)) !== null) set.add(m[1]);

      SQL_KEYWORDS.forEach((k) => set.add(k));

      return [...set];
    },
    hint(cm) {
      const cur = cm.getCursor();
      const linha = cm.getLine(cur.line);
      let start = cur.ch;
      while (start > 0 && /[\w]/.test(linha.charAt(start - 1))) start--;
      const palavra = linha.slice(start, cur.ch);
      const extras = this.hintExtras(cur, linha, start, palavra);

      if (extras && extras.prefixado) return extras.resultado;
      if (!palavra) return extras ? extras.resultado : null;

      const alvo = palavra.toUpperCase();
      const lista = this.candidatos()
        .filter((c) => c.toUpperCase().startsWith(alvo) && c.toUpperCase() !== alvo)
        .slice(0, 50);

      if (extras) {
        if (!lista.length) return extras.resultado;

        return {
          list: [...extras.resultado.list, ...lista].slice(0, 50),
          from: CodeMirror.Pos(cur.line, start),
          to: CodeMirror.Pos(cur.line, cur.ch),
        };
      }

      if (!lista.length) return null;

      return {
        list: lista,
        from: CodeMirror.Pos(cur.line, start),
        to: CodeMirror.Pos(cur.line, cur.ch),
      };
    },
    fecharSugestoesAoRolar(evento) {
      const sugestoes = this.cm && this.cm.state.completionActive;

      if (!sugestoes) return;

      const alvo = evento.target;

      if (alvo && alvo.nodeType === 1 && (alvo.closest(".CodeMirror-hints") || this.cm.getWrapperElement().contains(alvo))) return;

      sugestoes.close();
    },
    hintExtras(cur, linha, start, palavra) {
      if (!this.sugestoesExtras.length) return null;

      const prefixado = linha.charAt(start - 1) === ":" && linha.charAt(start - 2) !== ":";

      if (linha.charAt(start - 1) === ":" && !prefixado) return null;

      const alvo = palavra.toUpperCase();
      const list = this.sugestoesExtras
        .filter((sugestao) => {
          const nome = sugestao.texto.replace(/^:/, "").toUpperCase();

          return nome.startsWith(alvo) && nome !== alvo;
        })
        .slice(0, 50)
        .map((sugestao) => ({
          text: sugestao.texto,
          displayText: sugestao.descricao ? `${sugestao.texto}  ·  ${sugestao.descricao}` : sugestao.texto,
        }));

      if (!list.length) return null;

      return {
        prefixado,
        resultado: {
          list,
          from: CodeMirror.Pos(cur.line, prefixado ? start - 1 : start),
          to: CodeMirror.Pos(cur.line, cur.ch),
        },
      };
    },
    getCmEditor() {
      return this.cm;
    },
    // Insere texto na posição do cursor (usado pelos chips de CAMPOS).
    inserirNoCursor(texto) {
      if (!this.cm) return;
      this.cm.replaceSelection(texto);
      this.cm.focus();
    },
    // Formata/identa o SQL no estilo "river" (ver sqlRiverFormatter.js).
    // Se houver trecho selecionado, formata apenas a seleção; caso contrário,
    // formata o documento inteiro. Em caso de falha, mantém o SQL original.
    formatar() {
      if (this.readonly) return;
      // Formatação "river" é específica de SQL; não rodar em scripts PHP.
      if (this.ehPhp) return;
      const cm = this.cm;
      try {
        // Apenas a parte selecionada.
        if (cm && cm.somethingSelected()) {
          const sel = cm.getSelection();
          if (!sel.trim()) return;
          const out = formatRiver(sel);
          if (out !== sel) cm.replaceSelection(out, "around");
          cm.focus();
          return;
        }
        // Documento inteiro.
        const sql = (this.codigo || "").trim();
        if (!sql) return;
        const formatado = formatRiver(this.codigo);
        if (formatado === this.codigo) return;
        this.codigo = formatado;
        // Reaplica no editor e leva o cursor para o início após o reflow.
        this.$nextTick(() => {
          if (!this.cm) return;
          this.cm.setCursor({ line: 0, ch: 0 });
          this.cm.refresh();
          this.cm.focus();
        });
      } catch (e) {
        this.$swal({
          toast: true,
          position: "top-end",
          icon: "warning",
          title: "Não foi possível formatar",
          text: "Verifique a sintaxe do SQL e tente novamente.",
          showConfirmButton: false,
          timer: 3500,
          timerProgressBar: true,
        });
      }
    },
  },
};
</script>

<style scoped>
/* Tema do editor — via >>> para alcançar o DOM interno do CodeMirror sem vazar */
.sql-editor >>> .cm-s-vtdark.CodeMirror {
  background: #1e2030;
  color: #cdd2e6;
  height: 100%;
}
.sql-editor >>> .cm-s-vtdark .CodeMirror-gutters {
  background: #24273a;
  border-right: 1px solid #2d3148;
}
.sql-editor >>> .cm-s-vtdark .CodeMirror-linenumber { color: #6b7089; }
.sql-editor >>> .cm-s-vtdark .CodeMirror-cursor { border-left: 1px solid #cdd2e6; }
.sql-editor >>> .cm-s-vtdark .CodeMirror-activeline-background { background: #2d3148; }
.sql-editor >>> .cm-s-vtdark .CodeMirror-selected { background: #363b58 !important; }

.sql-editor >>> .cm-s-vtdark .cm-vt-kw      { color: #ef7aa3; }
.sql-editor >>> .cm-s-vtdark .cm-vt-fn      { color: #74b8ff; }
.sql-editor >>> .cm-s-vtdark .cm-vt-col     { color: #c9a4ff; }
.sql-editor >>> .cm-s-vtdark .cm-vt-tbl     { color: #e6ebff; }
.sql-editor >>> .cm-s-vtdark .cm-vt-ident   { color: #cdd2e6; }
.sql-editor >>> .cm-s-vtdark .cm-vt-num     { color: #f5a97f; }
.sql-editor >>> .cm-s-vtdark .cm-vt-str     { color: #9ed99a; }
.sql-editor >>> .cm-s-vtdark .cm-vt-punc    { color: #b6bce0; }
.sql-editor >>> .cm-s-vtdark .cm-vt-comment { color: #6b7089; font-style: italic; }

.sql-editor {
  display: flex;
  flex-direction: column;
  height: 100%;
  border-radius: 10px;
  overflow: hidden;
  background: #1e2030;
  border: 1px solid #2d3148;
}

.sql-editor-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: #24273a;
  border-bottom: 1px solid #2d3148;
}

.bar-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.sql-badge {
  background: #2298a3;
  color: #fff;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.04em;
  border-radius: 5px;
  padding: 2px 8px;
}

/* PHP usa o roxo característico do logo do PHP, distinguindo do SQL (teal). */
.sql-badge--php {
  background: #8892bf;
}

.sql-lines {
  color: #949aa8;
  font-size: 12px;
}

.bar-right {
  display: flex;
  gap: 8px;
}

.bar-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.06);
  color: #cdd2e6;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 12px;
  cursor: pointer;
}

.bar-btn:hover:not(:disabled) { background: rgba(255, 255, 255, 0.12); }
.bar-btn:disabled { opacity: 0.45; cursor: not-allowed; }

.sql-cm {
  flex: 1;
  min-height: 0;
  overflow: auto;
}

.sql-cm >>> .CodeMirror {
  height: 100%;
}

.fs-actions {
  position: fixed;
  bottom: 16px;
  right: 16px;
  z-index: 20;
  display: flex;
  gap: 8px;
}

.fs-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #24273a;
  color: #cdd2e6;
  border: 1px solid #2d3148;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 13px;
  cursor: pointer;
}

.fs-btn--run {
  background: #2298a3;
  color: #fff;
  border-color: #1a7a83;
}
</style>
